import { authenticatedFetch } from "./authenticatedFetch";
import { formatApiErrorMessage } from "../utils/apiErrorMessage";
import {
  isHtmlApiResponse,
  parseApiResponseBody,
} from "../utils/parseApiResponseBody";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

async function parseError(response, fallback) {
  const errorBody = await parseApiResponseBody(response);
  if (isHtmlApiResponse(response, errorBody)) {
    return formatApiErrorMessage(errorBody, fallback);
  }
  return formatApiErrorMessage(errorBody, fallback);
}

class CrmService {
  async listContacts() {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/contacts`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao listar leads"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.contacts ?? [] };
    } catch (error) {
      console.error("listContacts error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao listar leads"),
      };
    }
  }

  async getContact(id) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/crm/contacts/${encodeURIComponent(id)}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao buscar lead"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.contact };
    } catch (error) {
      console.error("getContact error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao buscar lead"),
      };
    }
  }

  async saveContact(payload) {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/contacts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao salvar lead"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.contact };
    } catch (error) {
      console.error("saveContact error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao salvar lead"),
      };
    }
  }

  async deleteContact(id) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/crm/contacts/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao excluir lead"),
        };
      }

      return { success: true };
    } catch (error) {
      console.error("deleteContact error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao excluir lead"),
      };
    }
  }

  async listStages() {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/stages`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao listar estágios"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.stages ?? [] };
    } catch (error) {
      console.error("listStages error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao listar estágios"),
      };
    }
  }

  async saveStage(payload) {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/stages`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao renomear estágio"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.stage };
    } catch (error) {
      console.error("saveStage error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao renomear estágio"),
      };
    }
  }

  async listCategories() {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/categories`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao listar categorias"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.categories ?? [] };
    } catch (error) {
      console.error("listCategories error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao listar categorias"),
      };
    }
  }

  async saveCategory(payload) {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/categories`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao salvar categoria"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.category };
    } catch (error) {
      console.error("saveCategory error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao salvar categoria"),
      };
    }
  }

  async deleteCategory(id) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/crm/categories/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao excluir categoria"),
        };
      }

      return { success: true };
    } catch (error) {
      console.error("deleteCategory error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao excluir categoria"),
      };
    }
  }

  async listTemplates() {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/templates`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao listar respostas"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.templates ?? [] };
    } catch (error) {
      console.error("listTemplates error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao listar respostas"),
      };
    }
  }

  async saveTemplate(payload) {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/crm/templates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao salvar resposta"),
        };
      }

      const data = await response.json();
      return { success: true, data: data.template };
    } catch (error) {
      console.error("saveTemplate error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao salvar resposta"),
      };
    }
  }

  async deleteTemplate(id) {
    try {
      const response = await authenticatedFetch(
        `${API_BASE_URL}/crm/templates/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!response.ok) {
        return {
          success: false,
          error: await parseError(response, "Erro ao excluir resposta"),
        };
      }

      return { success: true };
    } catch (error) {
      console.error("deleteTemplate error:", error);
      return {
        success: false,
        error: formatApiErrorMessage(error, "Erro inesperado ao excluir resposta"),
      };
    }
  }
}

export default new CrmService();
